import { DateTime } from "luxon";

export enum PlanOfCareAction {
  ATTACH_ONLY = "attach_only",
  CREATE = "create",
  SKIP = "skip",
  UPDATE = "update",
}

export interface PlanOfCareCandidate {
  certificationId: string;
  effectiveDate: string | null;
  approvalDate: string | null;
  hasDocument: boolean;
}

export interface PlanOfCareIntake {
  dateOfEvent: string;
  dateSigned: string;
  maxEffectiveDateGapDays: number;
}

export type PlanOfCareDecision =
  | { action: PlanOfCareAction.CREATE }
  | {
      action: PlanOfCareAction.ATTACH_ONLY | PlanOfCareAction.SKIP | PlanOfCareAction.UPDATE;
      certificationId: string;
    };

// Placeholder approval dates (01/01/1900, 12/31/9999, …) are not real; typos like 2926 are.
const PLACEHOLDER_MAX_EARLY_YEAR = 1900;
const PLACEHOLDER_MIN_LATE_YEAR = 9999;
const COMPLETE_ISO_DATE = /^\d{4}-\d{2}-\d{2}(?:T.*)?$/;

function parseCalendarDate(value: string): DateTime | null {
  // Luxon fills in a day for partial ISO values like "2026-10", so require a full date.
  const iso = COMPLETE_ISO_DATE.test(value) ? DateTime.fromISO(value, { zone: "utc", setZone: true }) : null;

  if (iso?.isValid) return iso;
  const us = DateTime.fromFormat(value, "M/d/yyyy", { zone: "utc" });

  return us.isValid ? us : null;
}

export function toRealPlanOfCareDate(value: string | null | undefined): string | null {
  const trimmed = value?.trim();

  if (!trimmed) return null;
  const date = parseCalendarDate(trimmed);

  if (!date || date.year <= PLACEHOLDER_MAX_EARLY_YEAR || date.year >= PLACEHOLDER_MIN_LATE_YEAR) return null;

  return date.toISODate();
}

function isBlank(value: string | null): boolean {
  return !value?.trim();
}

function compareIdsDescending(a: string, b: string): number {
  return b.length - a.length || b.localeCompare(a);
}

function highestId(candidates: PlanOfCareCandidate[]): string {
  return [...candidates].sort((a, b) => compareIdsDescending(a.certificationId, b.certificationId))[0].certificationId;
}

function daysBetween(a: string, b: string): number {
  return Math.abs(DateTime.fromISO(a, { zone: "utc" }).diff(DateTime.fromISO(b, { zone: "utc" }), "days").days);
}

export function selectPlanOfCareAction(
  candidates: PlanOfCareCandidate[],
  intake: PlanOfCareIntake
): PlanOfCareDecision {
  const matching = candidates.filter((entry) => toRealPlanOfCareDate(entry.approvalDate) === intake.dateSigned);
  const matchingUnattached = matching.filter((entry) => !entry.hasDocument);

  if (matchingUnattached.length > 0) {
    return { action: PlanOfCareAction.ATTACH_ONLY, certificationId: highestId(matchingUnattached) };
  }

  if (matching.length > 0) return { action: PlanOfCareAction.SKIP, certificationId: highestId(matching) };

  const updatable = candidates.flatMap((entry) => {
    const effectiveDate = toRealPlanOfCareDate(entry.effectiveDate);

    if (entry.hasDocument || !effectiveDate) return [];
    const gapDays = daysBetween(effectiveDate, intake.dateOfEvent);
    const approvalDate = toRealPlanOfCareDate(entry.approvalDate);
    const approvalReplaceable = isBlank(entry.approvalDate) || approvalDate === null || approvalDate === effectiveDate;

    return gapDays <= intake.maxEffectiveDateGapDays && approvalReplaceable ? [{ entry, gapDays }] : [];
  });

  if (updatable.length === 0) return { action: PlanOfCareAction.CREATE };

  const [best] = updatable.sort(
    (a, b) =>
      a.gapDays - b.gapDays ||
      Number(isBlank(b.entry.approvalDate)) - Number(isBlank(a.entry.approvalDate)) ||
      compareIdsDescending(a.entry.certificationId, b.entry.certificationId)
  );

  return { action: PlanOfCareAction.UPDATE, certificationId: best.entry.certificationId };
}
